"use client"

import { motion, useReducedMotion } from "framer-motion"
import Link from "next/link"
import { Bot, Timer, FileText, ChevronDown, Sparkles, type LucideIcon } from "lucide-react"
import { COPY, type WizardLang } from "@/components/ai-blueprint/copy"

// Map the copy's emoji markers to crisp Lucide icons - vector icons read far
// more premium than emoji (which render inconsistently across OSes).
const DELIVERABLE_ICONS: Record<string, LucideIcon> = {
  "🤖": Bot,
  "⏱️": Timer,
  "📄": FileText,
}

export default function AiBlueprintHero({ lang }: { lang: WizardLang }) {
  const t = COPY[lang].hero
  const dir = lang === "he" ? "rtl" : "ltr"
  const reduce = useReducedMotion()

  // Single source of entrance motion - respects prefers-reduced-motion.
  const fadeUp = {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: { opacity: 1, y: 0 },
  }
  // Stagger children ~70ms apart for a polished, sequenced reveal.
  const step = (i: number) => ({
    variants: fadeUp,
    initial: "hidden" as const,
    animate: "visible" as const,
    transition: { duration: 0.5, delay: 0.08 + i * 0.07, ease: [0.22, 1, 0.36, 1] as const },
  })

  return (
    <section
      dir={dir}
      className="relative isolate flex w-full justify-center overflow-hidden px-4 pt-24 pb-10 md:pt-36 md:pb-16"
    >
      {/* background flourish - purple-led to match the home hero identity */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -z-10 h-[620px] w-[900px] -translate-x-1/2 rounded-full bg-[#988BFD]/20 blur-[150px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-10 left-1/2 -z-10 h-[360px] w-[560px] -translate-x-1/2 rounded-full bg-[#988BFD]/12 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 right-0 -z-10 h-[280px] w-[460px] rounded-full bg-[#24D56D]/12 blur-[130px]"
      />

      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        {/* Eyebrow - trust signal lifted above the headline to set credibility first */}
        <motion.div
          {...step(0)}
          className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-1.5 backdrop-blur-sm"
        >
          <Sparkles className="h-3.5 w-3.5 text-[#24D56D]" aria-hidden />
          <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-cream/70 md:text-xs">
            {t.trustLine}
          </span>
        </motion.div>

        {/* Headline - two lines: the assertion, then the relatable problem */}
        <motion.h1
          {...step(1)}
          className="mt-6 font-montserrat font-black uppercase leading-[1.04] tracking-tight"
          style={{ fontSize: "clamp(1.85rem, 6.2vw, 3.6rem)" }}
        >
          <span className="block text-cream">{t.headline.line1}</span>
          <span className="mt-1 block text-cream/55">{t.headline.line2}</span>
        </motion.h1>

        {/* Subline - explanatory line directly under the headline */}
        <motion.p
          {...step(2)}
          className="mt-5 max-w-2xl text-base leading-relaxed text-cream/75 md:text-lg"
        >
          {t.subline}
        </motion.p>

        {/* Positioning band - the core contrast: SaaS assists, an AI agent does the work. */}
        <motion.div
          {...step(3)}
          className="mt-8 w-full max-w-xl overflow-hidden rounded-2xl border border-white/12 bg-white/[0.03] shadow-[0_8px_40px_rgba(0,0,0,0.25)] backdrop-blur-sm"
        >
          <div className="flex items-center gap-3 px-5 py-3.5 text-sm text-cream/55 md:text-base">
            <span className="shrink-0 rounded-full border border-white/20 px-2.5 py-0.5 text-[11px] font-bold tracking-wide text-cream/60 md:text-xs">
              SaaS
            </span>
            <span className="text-start leading-snug">{t.positioning.saas}</span>
          </div>
          <div className="h-px w-full bg-white/10" />
          <div className="flex items-center gap-3 bg-[#988BFD]/[0.08] px-5 py-3.5 text-sm md:text-base">
            <span className="shrink-0 rounded-full bg-[#988BFD]/20 px-2.5 py-0.5 text-[11px] font-bold tracking-wide text-[#988BFD] md:text-xs">
              AI Agent
            </span>
            <span className="text-start font-semibold leading-snug text-purple2 text-glow-purple">
              {t.positioning.agent}
            </span>
          </div>
        </motion.div>

        {/* Deliverables */}
        <motion.div
          {...step(4)}
          className="mt-9 grid w-full max-w-4xl gap-3 sm:gap-4 md:mt-12 sm:grid-cols-3"
        >
          {t.deliverables.map((d) => {
            const Icon = DELIVERABLE_ICONS[d.icon]
            return (
              <div
                key={d.title}
                className="group relative overflow-hidden rounded-2xl border border-white/15 bg-white/[0.04] p-5 text-start backdrop-blur-sm transition duration-300 hover:-translate-y-0.5 hover:border-[#24D56D]/50 hover:bg-white/[0.06]"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[#24D56D]/10 blur-2xl transition group-hover:bg-[#24D56D]/20"
                />
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#24D56D]/12 text-[#24D56D] ring-1 ring-inset ring-[#24D56D]/25">
                  {Icon ? <Icon className="h-5 w-5" strokeWidth={2} aria-hidden /> : <span className="text-2xl">{d.icon}</span>}
                </div>
                <div className="mt-3.5 text-base font-bold text-cream">{d.title}</div>
                <div className="mt-1.5 text-sm leading-relaxed text-cream/65">{d.desc}</div>
              </div>
            )
          })}
        </motion.div>

        {/* Primary CTA */}
        <motion.div {...step(5)} className="mt-9 md:mt-12">
          <Link
            href={`/${lang}/ai-blueprint/start`}
            className="group inline-flex items-center gap-3 rounded-full bg-[#24D56D] px-8 py-4 text-sm font-bold text-black shadow-[0_8px_32px_rgba(36,213,109,0.35)] transition hover:scale-[1.02] hover:shadow-[0_12px_40px_rgba(36,213,109,0.45)] md:text-base"
          >
            <span>{t.scrollCue}</span>
            <ChevronDown className="h-5 w-5 animate-bounce" aria-hidden />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
